<template>
  <div class="flex gap-16">
    <div class="flex-1">
      <RevInputMultiSelect
        id="market-codes"
        v-model="marketCodesInput"
        :description="
          i18n(translations.selectedValues, {
            numberOfSelectedValues: marketCodesInput.length,
          })
        "
        :label="i18n(translations.marketCodes)"
        :options="marketCodesOptions"
      />
    </div>
    <div class="flex-1">
      <RevInputMultiSelect
        id="categories"
        v-model="categoriesInput"
        :description="
          i18n(translations.selectedValues, {
            numberOfSelectedValues: categoriesInput.length,
          })
        "
        :label="i18n(translations.categories)"
        :options="categoriesOptions"
      />
    </div>
    <div class="flex-1">
      <RevInputMultiSelect
        id="grades"
        v-model="gradesInput"
        :description="
          i18n(translations.selectedValues, {
            numberOfSelectedValues: gradesInput.length,
          })
        "
        :label="i18n(translations.grades)"
        :options="gradesOptions"
      />
    </div>
    <div class="flex-1">
      <RevInputMultiSelect
        id="competition-levels"
        v-model="competitionLevelsInput"
        :description="
          i18n(translations.selectedValues, {
            numberOfSelectedValues: competitionLevelsInput.length,
          })
        "
        :label="i18n(translations.competitionLevels)"
        :options="competitionLevelOptions"
      />
    </div>
    <div class="flex-1">
      <RevInputText
        id="brand"
        v-model="brandInput"
        :label="i18n(translations.brand)"
      />
    </div>
    <div>
      <RevButton variant="primary" @click="applyFilters">
        {{ i18n(translations.apply) }}
      </RevButton>
    </div>
    <div>
      <RevButton variant="secondary" @click="resetFilters">
        {{ i18n(translations.reset) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevInputMultiSelect } from '@ds/components/InputMultiSelect'
import { RevInputText } from '@ds/components/InputText'

import { useOpportunitiesFilters } from '../composables/useOpportunitiesFilters'

import translations from './OpportunitiesFilters.translations'

const marketCodes = defineModel<string[]>('marketCodes', { required: true })
const brand = defineModel<string>('brand', { required: true })
const competitionLevels = defineModel<string[]>('competitionLevels', {
  required: true,
})
const categories = defineModel<string[]>('categories', { required: true })
const grades = defineModel<string[]>('grades', { required: true })

const marketCodesInput = ref<string[]>(marketCodes.value)
const brandInput = ref<string>(brand.value)
const competitionLevelsInput = ref<string[]>(competitionLevels.value)
const categoriesInput = ref<string[]>(categories.value)
const gradesInput = ref<string[]>(grades.value)

const emit = defineEmits(['reset'])

const i18n = useI18n()
const { trackClick } = useTracking()

watch([marketCodes, brand, competitionLevels, categories, grades], () => {
  marketCodesInput.value = marketCodes.value
  brandInput.value = brand.value
  competitionLevelsInput.value = competitionLevels.value
  categoriesInput.value = categories.value
  gradesInput.value = grades.value
})

const {
  marketCodesOptions,
  categoriesOptions,
  gradesOptions,
  competitionLevelOptions,
} = useOpportunitiesFilters()

function applyFilters() {
  trackClick({
    name: 'apply_filters',
    zone: 'opportunities_filters',
    value: {
      marketCodes: marketCodesInput.value,
      brand: brandInput.value,
      competitionLevels: competitionLevelsInput.value,
      categories: categoriesInput.value,
      grades: gradesInput.value,
    },
  })

  marketCodes.value = marketCodesInput.value
  brand.value = brandInput.value
  competitionLevels.value = competitionLevelsInput.value
  categories.value = categoriesInput.value
  grades.value = gradesInput.value
}

function resetFilters() {
  emit('reset')
  trackClick({
    name: 'reset_filters',
    zone: 'opportunities_filters',
  })
}
</script>
